import * as THREE from 'three';
import { buildWeaponModel, WEAPON_BY_NUM, WEAPON_VOXEL, GADGET_BY_NUM, type VoxelModel, type WeaponPartName } from '@vm/content';
import { buildModelGeometry, modelMaterials, type ModelGeometry } from './voxelModel';

export interface WeaponPartGeometry {
  name: WeaponPartName;
  geo: ModelGeometry;
  /** The part's fine hardware (null: none), pivoting with it. */
  detail: ModelGeometry | null;
  /** Pivot relative to the grip (meters). */
  pivot: THREE.Vector3;
  /** Unit direction the part leaves the weapon during reloads. */
  dir: THREE.Vector3;
  axis: 'x' | 'y' | 'z';
}

export interface CachedWeapon {
  model: VoxelModel;
  /** The complete model (third person, menus). */
  geo: ModelGeometry;
  /** Body without animated parts (view model). */
  body: ModelGeometry;
  /** The fitted sight (finer voxels on top of the gun), drawn with the body; null with factory sights. */
  attach: ModelGeometry | null;
  /** The gun's own fine hardware, whole (menus, third person); null when the design has none. */
  detail: ModelGeometry | null;
  /** The fine hardware without the animated parts' share (view model body). */
  detailBody: ModelGeometry | null;
  parts: WeaponPartGeometry[];
  /** Points relative to the grip, meters. */
  muzzle: THREE.Vector3;
  fore: THREE.Vector3 | null;
  sight: THREE.Vector3;
  length: number;
  /** Distance from the grip to the rear end (meters, +Z). */
  rear: number;
  /** Height of the model top above the grip (meters). */
  top: number;
  /** A fitted see-through sight (the eye aims through its window), or null. */
  optic: { seeThrough: boolean; relief: number } | null;
}

const cache = new Map<string, CachedWeapon>();

function glowFlags(model: VoxelModel): Uint8Array {
  const glow = new Uint8Array(256);
  for (const e of model.emissive) glow[e] = 1;
  return glow;
}

const rel = (p: readonly number[], grip: readonly number[]): THREE.Vector3 =>
  new THREE.Vector3((p[0] - grip[0]) * WEAPON_VOXEL, (p[1] - grip[1]) * WEAPON_VOXEL, (p[2] - grip[2]) * WEAPON_VOXEL);

/** Geometry for a weapon (optionally with a camo), centered on its grip. Barrel points toward -Z. Cached per weapon + camo. */
export function weaponGeometry(num: number, camo?: string | null, scope?: string | null): CachedWeapon | null {
  const key = `${num}:${camo ?? ''}:${scope && scope !== 'none' ? scope : ''}`;
  const hit = cache.get(key);
  if (hit) return hit;
  const def = WEAPON_BY_NUM[num];
  if (!def) return null;
  const model = buildWeaponModel(def.model, camo, scope);
  const glow = glowFlags(model);
  const { sx, sy, sz, grip } = model;
  const geo = buildModelGeometry(model.grid, sx, sy, sz, model.palette, glow, WEAPON_VOXEL, grip);
  const names = Object.keys(model.parts) as WeaponPartName[];
  let body = geo;
  const parts: WeaponPartGeometry[] = [];
  if (names.length) {
    const rest = model.grid.slice();
    for (const n of names) {
      const p = model.parts[n]!;
      for (let i = 0; i < p.grid.length; i++) if (p.grid[i]) rest[i] = 0;
      const pd = model.detail?.parts[n];
      parts.push({
        name: n,
        geo: buildModelGeometry(p.grid, sx, sy, sz, model.palette, glow, WEAPON_VOXEL, p.pivot),
        detail: pd && model.detail ? buildModelGeometry(pd, model.detail.sx, model.detail.sy, model.detail.sz, model.palette, glow, WEAPON_VOXEL / model.detail.res, [p.pivot[0] * model.detail.res, p.pivot[1] * model.detail.res, p.pivot[2] * model.detail.res]) : null,
        pivot: rel(p.pivot, grip),
        dir: new THREE.Vector3(p.dir[0], p.dir[1], p.dir[2]),
        axis: p.axis,
      });
    }
    body = buildModelGeometry(rest, sx, sy, sz, model.palette, glow, WEAPON_VOXEL, grip);
  }
  let detail: ModelGeometry | null = null;
  let detailBody: ModelGeometry | null = null;
  const dl = model.detail;
  if (dl) {
    const k = WEAPON_VOXEL / dl.res;
    const off: [number, number, number] = [grip[0] * dl.res, grip[1] * dl.res, grip[2] * dl.res];
    detail = buildModelGeometry(dl.grid, dl.sx, dl.sy, dl.sz, model.palette, glow, k, off);
    const rest = dl.grid.slice();
    for (const pg of Object.values(dl.parts)) if (pg) for (let i = 0; i < pg.length; i++) if (pg[i]) rest[i] = 0;
    detailBody = Object.keys(dl.parts).length ? buildModelGeometry(rest, dl.sx, dl.sy, dl.sz, model.palette, glow, k, off) : detail;
  }
  let attach: ModelGeometry | null = null;
  let top = (sy - grip[1]) * WEAPON_VOXEL;
  const at = model.attach;
  if (at) {
    const k = WEAPON_VOXEL / at.res;
    attach = buildModelGeometry(at.grid, at.sx, at.sy, at.sz, model.palette, glow, k, [(grip[0] - at.origin[0]) * at.res, (grip[1] - at.origin[1]) * at.res, (grip[2] - at.origin[2]) * at.res]);
    top = Math.max(top, (at.origin[1] - grip[1]) * WEAPON_VOXEL + at.sy * k);
  }
  const entry: CachedWeapon = {
    model,
    geo,
    body,
    attach,
    detail,
    detailBody,
    parts,
    muzzle: rel(model.muzzle, grip),
    fore: model.fore ? rel(model.fore, grip) : null,
    sight: rel(model.sight, grip),
    length: sz * WEAPON_VOXEL,
    rear: (sz - grip[2]) * WEAPON_VOXEL,
    top,
    optic: model.optic,
  };
  cache.set(key, entry);
  return entry;
}

function addMeshes(g: THREE.Group, geo: ModelGeometry, noFog: boolean, sight = false, role = ''): void {
  for (const [part, mat] of [[geo.lit, noFog ? modelMaterials.litNoFog : modelMaterials.lit], [geo.glow, noFog ? modelMaterials.glowNoFog : modelMaterials.glow]] as const) {
    if (!part) continue;
    const m = new THREE.Mesh(part, mat);
    // Marks the fitted sight's meshes: tools/ads-audit.mjs renders them alone to find the sight's window.
    if (sight) m.userData.sightMesh = true;
    m.userData.role = sight ? 'sight' : role;
    g.add(m);
  }
}

/** A mesh group for a complete weapon (lit + glow parts), origin at the grip. */
export function weaponObject(num: number, noFog = false, camo?: string | null, scope?: string | null): THREE.Group {
  const g = new THREE.Group();
  const w = weaponGeometry(num, camo, scope);
  if (!w) return g;
  addMeshes(g, w.geo, noFog);
  if (w.detail) addMeshes(g, w.detail, noFog);
  if (w.attach) addMeshes(g, w.attach, noFog, true);
  g.userData.muzzle = w.muzzle.clone();
  g.userData.rear = w.rear;
  g.userData.top = w.top;
  return g;
}

export interface RiggedWeapon {
  root: THREE.Group;
  /** Animated parts: a pivot group per part (rest transform = identity offset from `rest`). */
  parts: Partial<Record<WeaponPartName, { group: THREE.Group; rest: THREE.Vector3; dir: THREE.Vector3; axis: 'x' | 'y' | 'z' }>>;
  info: CachedWeapon;
}

/** A weapon split into its static body and animatable parts (view model). */
export function riggedWeapon(num: number, camo?: string | null, noFog = true, scope?: string | null): RiggedWeapon | null {
  const w = weaponGeometry(num, camo, scope);
  if (!w) return null;
  const root = new THREE.Group();
  addMeshes(root, w.body, noFog, false, 'body');
  if (w.detailBody) addMeshes(root, w.detailBody, noFog, false, 'detail');
  if (w.attach) addMeshes(root, w.attach, noFog, true);
  const parts: RiggedWeapon['parts'] = {};
  for (const p of w.parts) {
    const group = new THREE.Group();
    group.position.copy(p.pivot);
    addMeshes(group, p.geo, noFog, false, `part:${p.name}`);
    if (p.detail) addMeshes(group, p.detail, noFog, false, `part:${p.name}`);
    root.add(group);
    parts[p.name] = { group, rest: p.pivot.clone(), dir: p.dir.clone(), axis: p.axis };
  }
  return { root, parts, info: w };
}

/** Small colored cube used for thrown gadgets. */
export function gadgetObject(num: number): THREE.Mesh {
  const g = GADGET_BY_NUM[num];
  return new THREE.Mesh(new THREE.BoxGeometry(0.14, 0.14, 0.14), new THREE.MeshLambertMaterial({ color: g?.color ?? 0x5a7a3a }));
}
